import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { EVENT_TYPE } from '~/modules/common/enums/EventTypes';
import { useSnackbar } from '~/modules/common/snackbar';
import { DEFAULT_PAGE_SIZE } from '../hooks/useTaskResourceEstimates';
import { useOnSubmit } from '../hooks/useOnChangeHandlers';

const getSnackbarMessage = ({ isEdit, resource, role }) => {
  if (resource) {
    return {
      messageId: isEdit
        ? 'editResourceEstimateDialog.placeholderFilledSnackbarSuccessMessage'
        : 'addResourceEstimateDialog.resourceAddedSnackbarSuccessMessage',
      messageParams: { ResourceName: resource.displayText }
    };
  }

  return {
    messageId: isEdit
      ? 'editResourceEstimateDialog.placeholderUpdatedSnackbarSuccessMessage'
      : 'addResourceEstimateDialog.placeholderAddedSnackbarSuccessMessage',
    messageParams: isEdit ? {} : { RoleName: role?.displayText }
  };
};

const useResourceEstimateSubmit = ({
  task,
  canViewCost,
  resourceEstimates,
  setParentFieldValue,
  onClose
}) => {
  const { formatMessage } = useIntl();
  const { showSnackbar } = useSnackbar();

  const { onResourceEstimateSubmit: submitResourceEstimate } = useOnSubmit({
    taskId: task.id,
    loadTaskResourceUserAllocation: true,
    canViewCost
  });

  const onResourceEstimateSubmit = useCallback(
    async ({ input, resource, role, setSubmitting }) => {
      const isEdit = Boolean(input.taskResourceEstimateId);

      const { taskResourceEstimate } = await submitResourceEstimate(input, [
        {
          eventName: EVENT_TYPE.GANTT_TASK_RESOURCE_COUNT_UPDATE,
          payload: {
            taskId: task.id,
            updateResourceEstimatesCountBy: isEdit ? 0 : 1,
            updateResourceEstimatesWithUsersCountBy: resource ? 1 : 0
          }
        }
      ]);

      const estimateEntry = {
        ...taskResourceEstimate,
        initialEstimatedHours: input.initialEstimatedHours,
        ...(resource && {
          resourceUser: {
            ...resource,
            uri: taskResourceEstimate?.resourceUser?.uri
          }
        }),
        projectRole: role
      };

      const updatedResourceEstimates = isEdit
        ? resourceEstimates.map(estimate =>
            estimate?.id === estimateEntry?.id ? estimateEntry : estimate
          )
        : [estimateEntry, ...resourceEstimates];

      setParentFieldValue(
        'resourceEstimates',
        updatedResourceEstimates?.length > DEFAULT_PAGE_SIZE
          ? updatedResourceEstimates.slice(0, DEFAULT_PAGE_SIZE)
          : updatedResourceEstimates
      );

      const { messageId, messageParams } = getSnackbarMessage({
        isEdit,
        resource,
        role
      });

      showSnackbar &&
        showSnackbar(formatMessage({ id: messageId }, messageParams));
      setSubmitting(false);
      onClose();
    },
    [
      submitResourceEstimate,
      task.id,
      resourceEstimates,
      setParentFieldValue,
      onClose,
      showSnackbar,
      formatMessage
    ]
  );

  return { onResourceEstimateSubmit };
};

export default useResourceEstimateSubmit;
